import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CopyIcon, ExternalLinkIcon, LayoutTemplateIcon, LinkIcon, Loader2Icon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { createLanding, deleteLanding, duplicateLanding, setLandingStatus } from '../../services/landingService';
import { LANDING_TEMPLATES, LandingTemplateId } from '../../data/landingTemplates';
import { formatDate } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { EmptyState } from '../../components/crm/EmptyState';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { FloatingMenu } from '../../components/shared/FloatingMenu';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Tag } from '../../components/ui/Tag';
import { toast } from '../../components/ui/Toast';
import { copyToClipboard, landingUrl } from '../../utils/clipboard';
const STATUS_LABEL = {
  borrador: 'Borrador',
  publicada: 'Publicada',
  despublicada: 'Despublicada'
} as const;
export function Landings() {
  const navigate = useNavigate();
  const {
    organizationId,
    ready,
    landings,
    forms,
    leads
  } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [formId, setFormId] = useState('');
  const [template, setTemplate] = useState<LandingTemplateId>('caso_exito');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  async function handleCreate() {
    // Doble protección: el botón ya está deshabilitado hasta que el espacio de
    // trabajo termina de cargarse, pero un envío con Enter no debe colarse.
    if (!name.trim() || !ready || saving) return;
    setSaving(true);
    try {
      const landing = await createLanding(organizationId, name.trim(), formId || undefined, template);
      setCreating(false);
      setName('');
      setFormId('');
      setTemplate('caso_exito');
      navigate(`/app/landings/${landing.id}/editor`);
    } catch (error) {
      // El modal se mantiene abierto para poder reintentar sin perder datos.
      toast.error(error instanceof Error ? error.message : 'No hemos podido crear la landing.');
    } finally {
      setSaving(false);
    }
  }
  return <>
      <PageHeader title="Landings" description="Páginas públicas para captar potenciales clientes." actions={<Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1499" data-mp="3tp813y1jzdsp" />} onClick={() => setCreating(true)} data-id="element-1498" data-mp="3tog7gsrinq52">
            Nueva landing
          </Button>} data-id="element-1497" data-mp="3fxbgsg64m86v" />

      {landings.length === 0 ? <EmptyState icon={<LayoutTemplateIcon className="h-5 w-5" data-id="element-1501" data-mp="315ja0skee2gr" />} title="Todavía no tienes landings" description="Crea tu primera landing y conecta un formulario para empezar a captar potenciales clientes." action={<Button onClick={() => setCreating(true)} data-id="element-1502" data-mp="1box79xit2njn">Crear landing</Button>} data-id="element-1500" data-mp="3162m3snd9wmv" /> : <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-1503" data-mp="1ggyrc5m7tik8">
          {landings.map(landing => {
        const form = forms.find(f => f.id === landing.formId);
        const landingLeads = leads.filter(l => l.origin.landingId === landing.id);
        const hasVideo = landing.pageConfig.blocks.some(b => b.type === 'video' && b.visible);
        return <li key={landing.id} className="flex flex-col rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1504" data-mp="1vehdd07idhnz">
                <div className="flex items-start justify-between gap-3" data-id="element-1505" data-mp="1zcym1o6kv07i">
                  <div className="min-w-0" data-id="element-1506" data-mp="1lfa16gcift2g">
                    <Link to={`/app/landings/${landing.id}/editor`} className="block truncate text-base font-semibold text-foreground hover:text-primary" data-id="element-1507" data-mp="1qecwva6lomxr">
                      {landing.name}
                    </Link>
                    <p className="mt-1 truncate text-sm text-muted-foreground" data-id="element-1508" data-mp="ezpupf05zorv">
                      {landingUrl(landing.slug)}
                    </p>
                  </div>
                  <FloatingMenu align="right" label="Acciones de la landing" trigger={<span className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-surface" data-id="element-1510" data-mp="pg9vup5ropvk">
                        ⋯
                      </span>} items={[{
              label: 'Editar',
              icon: <PencilIcon className="h-4 w-4" data-id="element-1511" data-mp="gflkk27rd3md" />,
              onSelect: () => navigate(`/app/landings/${landing.id}/editor`)
            }, {
              label: 'Ver landing',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-1512" data-mp="19755prnx0rr0" />,
              onSelect: () => window.open(`/${landing.slug}`, '_blank')
            }, {
              label: 'Copiar enlace',
              icon: <LinkIcon className="h-4 w-4" data-id="element-1513" data-mp="z7o1zfs84nxo" />,
              onSelect: async () => {
                const ok = await copyToClipboard(landingUrl(landing.slug));
                if (ok) toast.success('Enlace copiado');else toast.error('No se ha podido copiar el enlace');
              }
            }, {
              label: 'Duplicar',
              icon: <CopyIcon className="h-4 w-4" data-id="element-1514" data-mp="3etkt5ofexfid" />,
              onSelect: () => duplicateLanding(landing.id)
            }, {
              label: landing.status === 'publicada' ? 'Actualizar publicación' : 'Publicar',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-1515" data-mp="3ojw3pttlqmy8" />,
              onSelect: async () => {
                await setLandingStatus(landing.id, 'publicada');
                toast.success('Landing publicada correctamente', {
                  description: landingUrl(landing.slug)
                });
              }
            }, {
              label: 'Eliminar',
              icon: <Trash2Icon className="h-4 w-4" data-id="element-1516" data-mp="bcufcndl9y64" />,
              destructive: true,
              onSelect: () => setToDelete(landing.id)
            }]} data-id="element-1509" data-mp="pimmrrti6akr" />
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm" data-id="element-1517" data-mp="2877fd4ug19cn">
                  <div data-id="element-1518" data-mp="2d50itq4d3p98">
                    <dt className="text-xs text-muted-foreground" data-id="element-1519" data-mp="2d6bm7otwauzt">Leads</dt>
                    <dd className="font-semibold text-foreground" data-id="element-1520" data-mp="254b2xsr2tjj5">{landingLeads.length}</dd>
                  </div>
                  <div data-id="element-1521" data-mp="2069f6uipgw1c">
                    <dt className="text-xs text-muted-foreground" data-id="element-1522" data-mp="2f1pfhh9vpo66">Visitas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-1523" data-mp="2ezmfq5ixcgo9">{landing.views}</dd>
                  </div>
                  <div data-id="element-1524" data-mp="2a4z0yydq30mv">
                    <dt className="text-xs text-muted-foreground" data-id="element-1525" data-mp="2o243wpi53jdw">Formulario</dt>
                    <dd className="truncate text-muted-foreground" data-id="element-1526" data-mp="2o0163jaqwlym">
                      {form?.name ?? 'Sin asociar'}
                    </dd>
                  </div>
                  <div data-id="element-1527" data-mp="2xrnoq0pexnbe">
                    <dt className="text-xs text-muted-foreground" data-id="element-1528" data-mp="2xu1h21spklf9">Modificada</dt>
                    <dd className="text-muted-foreground" data-id="element-1529" data-mp="2stvu2yxdvn3i">{formatDate(landing.updatedAt)}</dd>
                  </div>
                </dl>

                <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4" data-id="element-1530" data-mp="32rjt4120vgrh">
                  <Tag data-id="element-1531" data-mp="3lfkgo3zi273y">{STATUS_LABEL[landing.status]}</Tag>
                  <Tag data-id="element-1532" data-mp="3lg3naojp992o">{hasVideo ? 'Con vídeo' : 'Sin vídeo'}</Tag>
                </div>
              </li>;
      })}
        </ul>}

      <Modal open={creating} onClose={() => setCreating(false)} title="Nueva landing" footer={<div className="flex justify-end gap-2" data-id="element-1534" data-mp="3cem8ralxxfg3">
            <Button variant="ghost" onClick={() => setCreating(false)} data-id="element-1535" data-mp="3hd7dnnc50nav">
              Cancelar
            </Button>
            <Button onClick={handleCreate} loading={saving} disabled={!name.trim() || !ready} data-id="element-1536" data-mp="32o1d9w1kcr1e">
              {ready ? 'Crear' : 'Cargando…'}
            </Button>
          </div>} data-id="element-1533" data-mp="3qcax4sf7v4pw">
        <div className="space-y-5" data-id="element-1537" data-mp="2toxu6s2ecbmw">
          {!ready && <p role="status" className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground" data-id="element-1538" data-mp="2tq8xyrvudje0">
              <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1539" data-mp="2jq8cazai2r6n" />
              Preparando tu espacio de trabajo…
            </p>}
          <fieldset data-id="element-1540" data-mp="2oo9vdk7xeqi4">
            <legend className="mb-2 text-sm font-medium text-foreground" data-id="element-1541" data-mp="2oojmjb4pj3pe">
              Plantilla de partida
            </legend>
            <div className="grid gap-3 sm:grid-cols-2" data-id="element-1542" data-mp="2z88bd23dpta5">
              {LANDING_TEMPLATES.map(option => {
              const active = template === option.id;
              return <button key={option.id} type="button" onClick={() => setTemplate(option.id)} aria-pressed={active} className={`rounded-xl border p-4 text-left transition-colors ${active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`} data-id="element-1543" data-mp="2v53cd9iic9q9">
                    <span className="block text-sm font-semibold text-foreground" data-id="element-1544" data-mp="2ghi4z3elnocj">
                      {option.name}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground" data-id="element-1545" data-mp="2bgkr330mhggu">
                      {option.description}
                    </span>
                  </button>;
            })}
            </div>
          </fieldset>
          <Input id="landing-name" label="Nombre interno" placeholder="Campaña inmobiliarias octubre" value={name} onChange={e => setName(e.target.value)} data-id="element-1546" data-mp="1xicoofkthmya" />
          <Select id="landing-form" label="Formulario asociado" placeholder="Selecciona un formulario" options={forms.map(f => ({
          value: f.id,
          label: f.name
        }))} value={formId} onChange={e => setFormId(e.target.value)} data-id="element-1547" data-mp="d2y0q3jsc7hc" />
        </div>
      </Modal>

      <ConfirmDialog open={Boolean(toDelete)} title="Eliminar landing" description="La página dejará de estar disponible públicamente. Esta acción no se puede deshacer." onCancel={() => setToDelete(null)} onConfirm={async () => {
      if (toDelete) await deleteLanding(toDelete);
      setToDelete(null);
    }} data-id="element-1548" data-mp="r0341vs46s8w" />
    </>;
}